import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {preheatIssueViewerPrimary, type IssueCoordinates} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {type RefObject, useEffect} from 'react'

import {preheatIssueViewerSecondary} from '../utils/issue-viewer-secondary-preheat'
import {runPreheatTasksWhenIdle} from '../utils/run-preheat-tasks-when-idle'

async function preloadSecondaryComponents() {
  const {preheatIssueViewerSecondaryComponents} = await import('@github-ui/issue-viewer/preload-components')
  await preheatIssueViewerSecondaryComponents()
}

export function usePreheatIssueRows({
  issues,
  login,
  source,
  preloadComponents = preloadSecondaryComponents,
  intentContainerRef,
}: {
  issues: readonly IssueCoordinates[]
  login: string
  source: string
  preloadComponents?: () => Promise<void>
  intentContainerRef?: RefObject<HTMLDivElement | null>
}) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  useEffect(() => {
    const controller = new AbortController()
    const {signal} = controller
    const canPreheat = () =>
      !signal.aborted &&
      isLoggedIn() &&
      getCurrentUserLogin() === login &&
      !isFeatureEnabled('disable_preheating_for_issues')

    if (!canPreheat()) return
    const onError = () => {
      if (canPreheat())
        sendAnalyticsEvent('offline_cache.preheat_error', undefined, {mode: 'tanstack', operation: 'task'})
    }
    const whenAllowed = (preheat: () => Promise<void>) => async () => {
      if (canPreheat()) await preheat()
    }
    let componentPreload: Promise<void> | undefined
    const warmComponents = () => {
      if (!componentPreload) {
        componentPreload = (async () => {
          try {
            await preloadComponents()
          } catch {
            componentPreload = undefined
            onError()
          }
        })()
      }
      return componentPreload
    }

    // Code warming must not hold up later data batches while its chunks download.
    runPreheatTasksWhenIdle(
      [
        whenAllowed(async () => {
          void warmComponents()
        }),
        ...issues.map(issue => whenAllowed(() => preheatIssueViewerPrimary({...issue, source, signal, onError}))),
        ...issues.map(issue =>
          whenAllowed(() => preheatIssueViewerSecondary({...issue, source, sendAnalyticsEvent, signal, onError})),
        ),
      ],
      signal,
    )

    const container = intentContainerRef?.current
    const intentIssues = new Map(issues.map(issue => [JSON.stringify(issue), issue]))
    const preheatedIntents = new Set<string>()
    const onIntent = async (event: Event) => {
      if (!canPreheat() || !(event.target instanceof Element)) return
      const anchor = event.target.closest('a[href]')
      if (!anchor || !container?.contains(anchor)) return
      const coordinates = issueCoordinatesFromUrl(anchor.getAttribute('href'))
      if (!coordinates) return
      const key = JSON.stringify(coordinates)
      const issue = intentIssues.get(key)
      if (!issue || preheatedIntents.has(key)) return
      preheatedIntents.add(key)
      const onIntentError = () => {
        preheatedIntents.delete(key)
        onError()
      }
      try {
        await Promise.all([
          warmComponents(),
          preheatIssueViewerPrimary({...issue, source, signal, onError: onIntentError}),
          preheatIssueViewerSecondary({...issue, source, sendAnalyticsEvent, signal, onError: onIntentError}),
        ])
      } catch {
        onIntentError()
      }
    }
    container?.addEventListener('mouseover', onIntent, {signal, capture: true})
    container?.addEventListener('focusin', onIntent, {signal, capture: true})
    container?.addEventListener('touchstart', onIntent, {signal, capture: true, passive: true})
    return () => {
      controller.abort()
      container?.removeEventListener('mouseover', onIntent, true)
      container?.removeEventListener('focusin', onIntent, true)
      container?.removeEventListener('touchstart', onIntent, true)
    }
  }, [issues, login, source, sendAnalyticsEvent, preloadComponents, intentContainerRef])
}

export function issueCoordinatesFromUrl(rawHref: unknown): IssueCoordinates | undefined {
  if (typeof window === 'undefined' || typeof rawHref !== 'string') return
  try {
    const url = new URL(rawHref, window.location.href)
    if (url.origin !== window.location.origin) return
    const match = /^\/([^/]+)\/([^/]+)\/issues\/(\d+)$/.exec(url.pathname)
    if (!match?.[1] || !match[2] || !match[3]) return
    const owner = decodeURIComponent(match[1])
    const repo = decodeURIComponent(match[2])
    const number = Number(match[3])
    if (!owner || !repo || /[/\\]/.test(owner + repo) || !Number.isSafeInteger(number) || number <= 0) return
    return {owner, repo, number}
  } catch {
    // Malformed links are not preheat candidates.
    return
  }
}
